/**
 * The stitch look, separated from the editor that used to own it.
 *
 * StitchCanvas built these elements inline, which meant the only way to see a
 * design as thread rather than flat vector was to open the full editor. The
 * community gallery needs the same picture without any of the editing
 * machinery, so the pure part — decide each layer's passes, turn them into
 * clipped SVG paths — lives here and both callers share it.
 *
 * Everything in this file is a pure function of its inputs. No refs, no state,
 * no DOM measurement: given the artwork, the per-layer settings and a viewBox,
 * it returns React elements.
 */

import { FillPreviewMask } from "./FillPreviewMask";
import { RealisticFill } from "./RealisticFill";

import {
  computeSatinStitchD,
  computeFillStitchD,
  computeRunningStitchDs,
  computeMeanderStitchD,
  computeCircularStitchD,
  computeContourStitchDs,
} from "./stitchGeometry";

// ── Thread colouring ──────────────────────────────────────────────────────
// Each stitch is drawn as several stacked strokes: a shadow, a dark rim, the
// lit body and a specular core. These derive those tones from the layer colour.

export function parseHex(hex) {
  const m = hex.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return [128, 128, 128];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

export function rgbToHls(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;
  if (max === min) { h = 0; s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h, l, s];
}

export function hlsToRgb(h, l, s) {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1/3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1/3) * 255),
  ];
}

export function darkerHex(hex) {
  const [r, g, b] = parseHex(hex);
  let [h, l, s] = rgbToHls(r, g, b);
  // Shadowed rim/base of the thread. Darken strongly for mid & dark colours to
  // give depth, but only gently for light colours so white/cream thread doesn't
  // read as grey against a light fabric.
  let dl = l * 0.75;
  if (l > 0.6) {
    const t = (l - 0.6) / 0.4; // 0 at l=0.6 → 1 at l=1
    dl = dl + (l - dl) * t * 0.72; // pull back toward the true lightness
  }
  const [rr, gg, bb] = hlsToRgb(h, dl, s);
  return `rgb(${rr},${gg},${bb})`;
}

export function lightenHex(hex, amt) {
  const [r, g, b] = parseHex(hex);
  return `rgb(${Math.min(255, r + amt)},${Math.min(255, g + amt)},${Math.min(255, b + amt)})`;
}

export function threadBaseColor(hex) {
  const [r, g, b] = parseHex(hex);
  let [h, l, s] = rgbToHls(r, g, b);
  // Lit top of the thread. Keep light threads light (the old 0.85 cap turned
  // white thread grey); only hold pure white back a hair for definition.
  if (l > 0.97) l = 0.97;
  s = Math.min(1, s + 0.1);
  const [rr, gg, bb] = hlsToRgb(h, l, s);
  return `rgb(${rr},${gg},${bb})`;
}

/**
 * The editor's opening global settings, for callers that have per-layer state
 * but no editor around it.
 *
 * Only the fields getEffectiveLayerPasses actually falls back to are here. The
 * global block is never serialised with a design — see serializeStitchState —
 * so a design reopened by anyone, owner included, starts from these. That makes
 * them the correct baseline for a preview, not an approximation of one.
 */
export const DEFAULT_STITCH_SETTINGS = {
  stitch_type: "auto_fill",
  fill_method: "auto_fill",
  angle: null,
  row_spacing_mm: 0.2,
  zigzag_spacing_mm: 0.4,
  running_stitch_length_mm: 2.0,
};

/**
 * Resolves what a layer actually sews as, reconciling its declared stitch type
 * with whether it has rails to sew along.
 */
export function getEffectiveLayerPasses(layerIndex, { stitchSettings, embroidery, layerRails }) {
  const globalDefaults = {
    stitchType: stitchSettings.stitch_type || "legacy_fill",
    fillMethod: stitchSettings.fill_method || stitchSettings.stitch_type || "legacy_fill",
    max_stitch_length_mm: stitchSettings.max_stitch_length_mm ?? 3,
    staggers: stitchSettings.staggers ?? 4,
    randomize_stitch_length: stitchSettings.randomize_stitch_length ?? false,
    random_stitch_length_jitter_percent: stitchSettings.random_stitch_length_jitter_percent ?? 25,
    expand_mm: stitchSettings.expand_mm ?? 0,
    pull_compensation_mm: stitchSettings.pull_compensation_mm ?? 0.2,
    angle: stitchSettings.angle,
    row_spacing_mm: stitchSettings.row_spacing_mm || 0.35,
    running_stitch_length_mm: stitchSettings.running_stitch_length_mm || 2.5,
    zigzag_spacing_mm: stitchSettings.zigzag_spacing_mm || 0.4,
  };

  // Stitch type is authoritative: a layer only renders as satin if its own
  // pass type is satin. Leftover rails on a Fill/Running layer (e.g. a sublayer
  // cloned from a satin parent) are ignored, so the chosen type always wins.
  const perLayerForType = embroidery?.getLayerSettings?.(layerIndex);
  const explicitType =
    perLayerForType?.passes?.[perLayerForType?.activePassIndex || 0]?.stitchType ||
    perLayerForType?.passes?.[0]?.stitchType;
  const wantsNonSatin = !!explicitType && explicitType !== "satin";

  const entry = layerRails?.[layerIndex];
  const pairs = entry?.pairs || [];
  const hasMultiRails = pairs.some((p) => p.rail0 && p.rail1);
  const hasFlatRails = !!(entry?.rail0 && entry?.rail1);
  const defaultSatin = (hasMultiRails || hasFlatRails) && !wantsNonSatin;

  const noRails = !hasMultiRails && !hasFlatRails;
  const withFallback = (pass) => {
    if (pass.stitchType === "satin" && noRails) return { ...pass, stitchType: "legacy_fill", fillMethod: "legacy_fill" };
    return pass;
  };

  const perLayer = embroidery?.getLayerSettings?.(layerIndex);
  if (perLayer?.passes && perLayer.passes.length > 0) {
    return perLayer.passes.map((pass) => withFallback({
      stitchType: pass.stitchType || globalDefaults.stitchType,
      fillMethod: pass.fill_method || pass.stitchType || globalDefaults.fillMethod,
      max_stitch_length_mm: pass.max_stitch_length_mm ?? 3,
      staggers: pass.staggers ?? 4,
      randomize_stitch_length: pass.randomize_stitch_length ?? false,
      random_stitch_length_jitter_percent: pass.random_stitch_length_jitter_percent ?? 25,
      expand_mm: pass.expand_mm ?? 0,
      pull_compensation_mm: pass.pull_compensation_mm ?? 0.2,
      angle: pass.angle,
      row_spacing_mm: pass.row_spacing_mm ?? globalDefaults.row_spacing_mm,
      running_stitch_length_mm: pass.running_stitch_length_mm ?? globalDefaults.running_stitch_length_mm,
      zigzag_spacing_mm: pass.zigzag_spacing_mm ?? globalDefaults.zigzag_spacing_mm,
    }));
  }

  return [withFallback(defaultSatin ? { ...globalDefaults, stitchType: "satin" } : globalDefaults)];
}

/**
 * Builds the SVG <path> elements for every layer's stitch pattern, each clipped
 * by its layer shape. Vector rather than raster so it stays sharp at any zoom.
 *
 * @param {object} args
 * @param {object} args.svgJoined  Parsed artwork, one child per layer.
 * @param {object} [args.svgSplit] Same artwork split into subpaths — needed for
 *   running stitch and for satin that falls back to two detected rails.
 * @param {object} args.stitchSettings Global defaults; DEFAULT_STITCH_SETTINGS
 *   when there is no editor supplying them.
 * @param {string[]} args.colors Per-layer thread colour, indexed by layer.
 * @param {object} args.layerRails Per-layer satin rails.
 * @param {number|"all"} [args.layerSelected] Restrict to one layer.
 * @param {{vbX:number,vbY:number,vbW:number,vbH:number}} args.viewBox
 * @param {number} args.targetWidth Design width in mm — sets the mm-to-viewBox
 *   scale, so stitch density matches the physical size it was tuned at.
 * @param {object} [args.embroidery] Anything exposing getLayerSettings(index).
 * @param {string} [args.keyPrefix] Disambiguates clip ids when more than one
 *   render shares a document.
 * @returns {Array|null} elements, or null when there is nothing to draw.
 */
export function buildStitchElements({
  svgJoined,
  svgSplit,
  stitchSettings,
  colors,
  layerRails,
  layerSelected = "all",
  viewBox,
  targetWidth,
  embroidery,
  keyPrefix = "stitch",
}) {
  if (!svgJoined || !svgJoined.children?.length) return null;

  const { vbX, vbY, vbW, vbH } = viewBox;
  const mmToVb = vbW / (targetWidth || 100);
  const layerCount = svgJoined.children.length;
  const elements = [];

  for (let i = 0; i < layerCount; i++) {
    if (layerSelected !== "all" && i !== layerSelected) continue;
    const child = svgJoined.children[i];
    if (!child) continue;

    const layerColor = colors?.[i] || "#888888";
    const layerPasses = getEffectiveLayerPasses(i, { stitchSettings, embroidery, layerRails });

    // Collect all path d-strings from this layer for clipPath, preserving fill-rule
    const clipPaths = [];
    let layerFillRule = child.attributes?.["fill-rule"] || null;
    const walkPaths = (node) => {
      if (node?.attributes?.d) {
        const fr = node.attributes?.["fill-rule"];
        if (fr) layerFillRule = fr;
        clipPaths.push(node.attributes.d);
      }
      if (node?.children) node.children.forEach(walkPaths);
    };
    walkPaths(child);
    if (clipPaths.length === 0) continue;

    const clipId = `${keyPrefix}-clip-${i}`;
    const darkCol = darkerHex(layerColor);
    const baseCol = threadBaseColor(layerColor);
    const lightCol = lightenHex(layerColor, 140);

    for (let pi = 0; pi < layerPasses.length; pi++) {
      const settings = layerPasses[pi];
      const stitchType = settings.stitchType;
      const isFill = !["satin", "running"].includes(stitchType);
      const maskId = `${keyPrefix}-${i}-${pi}-compensation`;
      const margin = (Math.abs(settings.expand_mm || 0) + (settings.pull_compensation_mm ?? 0.2) + 0.5) * mmToVb;
      const bounds = { x: vbX - margin, y: vbY - margin, width: vbW + 2 * margin, height: vbH + 2 * margin };
      if (isFill) elements.push(<defs key={`${maskId}-defs`}>
        <FillPreviewMask id={maskId} paths={clipPaths} fillRule={layerFillRule || "nonzero"}
          settings={settings} scale={mmToVb} bounds={bounds} />
      </defs>);
      let stitchD = "";
      let isRunning = false;
      let runningDs = [];

      if (stitchType === "satin") {
        const railEntry = layerRails?.[i];
        const railPairs = railEntry?.pairs || [];
        let completePairs = railPairs.filter((p) => p.rail0 && p.rail1);
        if (completePairs.length === 0 && railEntry?.rail0 && railEntry?.rail1) {
          completePairs = [{ rail0: railEntry.rail0, rail1: railEntry.rail1, rungs: railEntry.rungs }];
        }
        if (completePairs.length > 0) {
          const satinSpVb = (settings.zigzag_spacing_mm || 0.4) * mmToVb;
          stitchD = completePairs.map((pair) =>
            computeSatinStitchD(pair.rail0, pair.rail1, satinSpVb, 1, vbW, (settings.pull_compensation_mm ?? 0.2) * mmToVb, pair.rungs || [])
          ).join("");
        } else {
          const splitChildren = svgSplit?.children?.[i]?.children;
          if (splitChildren?.length === 2) {
            const d0 = splitChildren[0]?.attributes?.d;
            const d1 = splitChildren[1]?.attributes?.d;
            const satinSpVb = (settings.zigzag_spacing_mm || 0.4) * mmToVb;
            if (d0 && d1) stitchD = computeSatinStitchD(d0, d1, satinSpVb, 1, vbW, (settings.pull_compensation_mm ?? 0.2) * mmToVb);
          }
        }
      } else if (stitchType === "running") {
        isRunning = true;
        const splitLayer = svgSplit?.children?.[i];
        runningDs = computeRunningStitchDs(
          splitLayer?.children?.map((c) => c.attributes?.d) || []
        );
      } else {
        const method = settings.fillMethod || "auto_fill";
        const spacingVb = (settings.row_spacing_mm || 0.35) * mmToVb;
        if (method === "meander_fill") {
          stitchD = computeMeanderStitchD(vbX, vbY, vbW, vbH, spacingVb);
        } else if (method === "circular_fill") {
          stitchD = computeCircularStitchD(vbX, vbY, vbW, vbH, spacingVb);
        } else if (method === "contour_fill") {
          stitchD = computeContourStitchDs(clipPaths, vbX, vbY, vbW, vbH, spacingVb);
        } else {
          stitchD = computeFillStitchD(vbX, vbY, vbW, vbH, settings.angle ?? 45, spacingVb);
        }
      }

      if (!stitchD && !isRunning) continue;
      if (isRunning && runningDs.length === 0) continue;

      const thickW = 0.23 * mmToVb;
      // Visual length of one stitch along a fill row, so rows read as a run
      // of needle penetrations rather than a single solid line.
      const penetrationVb = 2.4 * mmToVb;

      const passKey = `${keyPrefix}-${i}-${pi}`;

      if (isRunning) {
        const stitchLen = (settings.running_stitch_length_mm || 2.5) * mmToVb;
        const gapLen = Math.min(0.04 * mmToVb, stitchLen * 0.04);
        const rThickW = thickW;
        elements.push(
          <g key={passKey} clipPath={`url(#${clipId})`}>
            {runningDs.map((rd, ri) => (
              <g key={ri}>
                <path d={rd} fill="none" stroke={darkCol} strokeWidth={rThickW * 1.05}
                  strokeLinecap="round" strokeDasharray={`${stitchLen} ${gapLen}`} opacity="1" />
                <path d={rd} fill="none" stroke={baseCol} strokeWidth={rThickW * 0.78}
                  strokeLinecap="round" strokeDasharray={`${stitchLen} ${gapLen}`} opacity="0.9" />
                <path d={rd} fill="none" stroke={lightCol} strokeWidth={rThickW * 0.22}
                  strokeLinecap="round" strokeDasharray={`${stitchLen} ${gapLen}`} opacity="0.65"
                  transform={`translate(${-rThickW * 0.15} ${-rThickW * 0.15})`} />
              </g>
            ))}
          </g>
        );
      } else if (stitchType === "satin") {
        // Satin: dense threads bridging the column. A soft cast shadow adds
        // depth and a bright specular core makes it read glossy — the
        // signature shine that distinguishes satin from a matte fill.
        elements.push(
          <g key={passKey} strokeLinecap="round" strokeLinejoin="round">
            <path d={stitchD} fill="none" stroke="rgba(8,4,0,0.16)" strokeWidth={thickW * 1.55} />
            <path d={stitchD} fill="none" stroke={darkCol} strokeWidth={thickW * 1.18} opacity="0.85" />
            <path d={stitchD} fill="none" stroke={baseCol} strokeWidth={thickW * 0.9} opacity="0.95" />
            <path d={stitchD} fill="none" stroke={lightCol} strokeWidth={thickW * 0.38} opacity="0.38"
              transform={`translate(${-thickW * 0.14} ${-thickW * 0.14})`} />
            <path d={stitchD} fill="none" stroke={lightCol} strokeWidth={thickW * 0.1} opacity="0.7"
              transform={`translate(${-thickW * 0.2} ${-thickW * 0.2})`} />
          </g>
        );
      } else if (["auto_fill", "legacy_fill"].includes(settings.fillMethod || "auto_fill")) {
        elements.push(
          <RealisticFill key={passKey} id={passKey} clipId={clipId}
            settings={settings} mmToVb={mmToVb} color={layerColor}
            maskId={maskId} bounds={bounds} viewBox={viewBox} />
        );
      } else {
        // Fill: solid coverage, but each row is dashed into stitch-length
        // segments so individual needle penetrations show through — reads as
        // real embroidery instead of flat diagonal hatching.
        const dashArr = `${penetrationVb.toFixed(2)} ${(penetrationVb * 0.1).toFixed(2)}`;
        elements.push(
          <g key={passKey} mask={`url(#${maskId})`}>
            <path d={stitchD} fill="none" stroke="rgba(10,5,0,0.08)" strokeWidth={thickW * 1.45}
              strokeLinecap="butt" />
            <path d={stitchD} fill="none" stroke={darkCol} strokeWidth={thickW * 1.05}
              strokeLinecap="butt" opacity="0.62" strokeDasharray={dashArr} />
            <path d={stitchD} fill="none" stroke={baseCol} strokeWidth={thickW * 0.82}
              strokeLinecap="butt" opacity="0.55" strokeDasharray={dashArr} />
            <path d={stitchD} fill="none" stroke={lightCol} strokeWidth={Math.max(0.1, thickW * 0.3)}
              strokeLinecap="butt" opacity="0.22" strokeDasharray={dashArr} />
          </g>
        );
      }
    }

    // Add clipPath definition for this layer, preserving the original fill-rule
    // so compound paths with holes (evenodd) clip correctly.
    elements.unshift(
      <defs key={`${keyPrefix}-clip-def-${i}`}>
        <clipPath id={clipId}>
          {clipPaths.map((cd, ci) => (
            <path key={ci} d={cd} clipRule={layerFillRule || "nonzero"} />
          ))}
        </clipPath>
      </defs>
    );
  }

  return elements.length > 0 ? elements : null;
}
